/**
 * Shared builder: PL `HonestReviewsWidgetData` → domain `CustomSection`.
 *
 * Converts PL widget data to the CustomSection format for HonestReviewsV2.
 * Reviews arrive as nested widgets (REVIEW_CARD), enriched at PL serve time.
 */

import type { IWebsiteProduct } from '@src/components/product-card/product-card.types'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import GenericContent from '@src/quince-site-common/src/domains/GenericContent'

import { buildWidgetTrackingMetaData, toRichText } from './widget-utils'

import type { HonestReviewsWidgetData, WidgetChild } from '@lastbrand/storefront-proto-registry/api/v1/widget'

export interface ReviewCardData {
  id: string
  reviewText?: Record<string, unknown>
  authorText?: Record<string, unknown>
  product?: IWebsiteProduct
}

/** WidgetChild(REVIEW_CARD → reviewCard)[] → GenericContent[]. */
export function childrenToReviews(children: WidgetChild[] = []): GenericContent[] {
  return children
    .map(child => {
      const reviewCard = (child.data as { reviewCard?: ReviewCardData } | undefined)?.reviewCard
      if (!reviewCard) return null

      const reviewText = toRichText(reviewCard.reviewText)
      const authorText = toRichText(reviewCard.authorText)
      const product = reviewCard.product

      return new GenericContent({
        id: reviewCard.id,
        content: authorText,
        titleText: reviewText,
        products: product ? [product] : [],
        images: [],
        colors: [],
      })
    })
    .filter((review): review is GenericContent => Boolean(review))
}

export function buildHonestReviewsConfig(data: HonestReviewsWidgetData, reviews: GenericContent[]): CustomSection {
  const heading = toRichText(data.heading)
  return new CustomSection({
    id: data.id,
    sectionType: CUSTOM_SECTION_TYPE.HONEST_REVIEWS_V2,
    richTexts: heading ? [heading] : [],
    genericContents: reviews,
    classNames: [],
    influencers: [],
    layoutType: LAYOUT_TYPES.DEFAULT,
    baseDynamicClasses: [],
    mobileDynamicClassesOverrides: [],
    classNamesMobileOverrides: [],
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics),
  })
}
